// Resolve a single battle outcome into the run-state mutations dictated by
// docs/07-content §7.5.1 (HP carry-over + revive priority + reward penalty).
//
// Step 10b-3 wires the canonical 1-hero flow. Multi-hero parties (Phase 4
// dispatch) extend the same pure function — the signature already takes
// aggregate `allyHpSum` / `allyHpMaxSum` so adding allies is a caller-side
// summation change, not a resolver change.
//
// Reconciliation: docs/07 §7.5.1 and docs/10 §10.2 disagree on whether a
// revive-potion-consumed battle still incurs the −30% reward penalty.
// We pin docs/07 §7.5.1 here ("未所持時のみ penalty 適用") because docs/10
// §10.2 design intent ("ペナルティを経済リソースで吸収") only makes sense
// if the potion buys the player out of the penalty. A docs-level
// follow-up issue is owed.
//
// Phase 3 Step 10b-3.

import { aggregateDungeonBoons, type DungeonRunState } from '@renavora/shared';
import type { BattleOutcome } from '@renavora/sim';

/** Inventory id used to identify revive potions in `DungeonRunState.inventory`. */
export const REVIVE_POTION_ID = 'revive_potion';

/** Reward-penalty increment applied per dungeon-defeat-without-revive. */
export const PENALTY_INCREMENT = 0.3;

/** Reward-penalty hard cap (docs/07 §7.5.1 / docs/10 §10.2). */
export const PENALTY_CAP = 0.5;

/** Party HP ratio set when a revive potion succeeds. */
export const REVIVE_HP_RATIO = 0.5;

export interface BattleResolutionInfo {
  /** Sum of final HP across every ally entity in the battle. */
  readonly allyHpSum: number;
  /** Sum of HP-max across every ally entity in the battle. */
  readonly allyHpMaxSum: number;
  readonly outcome: BattleOutcome;
  /**
   * `true` when the battle was the dungeon's Floor 12 boss fight. On
   * victory the resolver flips `nextRun.cleared = true`; on defeat the
   * cleared field is left alone (boss is re-tryable until abandon). When
   * omitted the resolver behaves as in Step 10b-3 (non-boss battle).
   *
   * Phase 3 Step 10b-4.
   */
  readonly isBoss?: boolean;
}

export interface BattleResolutionOutcome {
  /** `null` only when the run must auto-abandon (anomaly path). */
  readonly nextRun: DungeonRunState | null;
  /** Mirror of nextRun?.partyHpRatio (or 0 when abandoned). */
  readonly partyHpRatio: number;
  readonly reviveConsumed: boolean;
  /** Actual penalty added to penaltyPct after cap clamping. */
  readonly penaltyApplied: number;
  /** `true` iff `nextRun === null`. */
  readonly abandoned: boolean;
}

/**
 * Remove the first occurrence of `id` from `items`, returning a fresh
 * array. When the id is absent, returns the input reference unchanged so
 * callers can detect "no-op" by reference equality.
 */
export function removeOneFromInventory(
  items: readonly string[],
  id: string,
): readonly string[] {
  const idx = items.indexOf(id);
  if (idx < 0) return items;
  return [...items.slice(0, idx), ...items.slice(idx + 1)];
}

/** Treat `allyWin` and `timeoutAlly` as victory. Everything else is defeat. */
export function isVictoryOutcome(outcome: BattleOutcome): boolean {
  return outcome === 'allyWin' || outcome === 'timeoutAlly';
}

/** Internal alias kept for readability inside resolveBattleResult. */
const isVictory = isVictoryOutcome;

/**
 * Compute the run-state update for a finished battle. Pure: no I/O, no
 * mutation of inputs.
 *
 * Semantics:
 *   - Victory → partyHpRatio = allyHpSum / allyHpMaxSum (clamped to >= 0).
 *     Penalty / inventory untouched.
 *   - Defeat AND allyHpSum > 0 (impossible in 1-hero scope; reserved for
 *     multi-hero) → same partyHpRatio update, no penalty applied.
 *   - Defeat AND allyHpSum === 0:
 *     - Revive potion in inventory → consume one, partyHpRatio = 0.5,
 *       penaltyApplied = 0.
 *     - No revive potion → partyHpRatio = 1 / allyHpMaxSum (HP=1),
 *       penaltyApplied = min(PENALTY_INCREMENT, PENALTY_CAP - run.penaltyPct).
 *   - Anomaly (`allyHpMaxSum === 0`): trigger auto-abandon (nextRun = null).
 */
export function resolveBattleResult(
  run: DungeonRunState,
  info: BattleResolutionInfo,
): BattleResolutionOutcome {
  if (info.allyHpMaxSum <= 0) {
    // Defensive: no ally entities at all means we cannot compute a ratio.
    // Treat as auto-abandon (anomaly path; unreachable in 1-hero scope).
    return {
      nextRun: null,
      partyHpRatio: 0,
      reviveConsumed: false,
      penaltyApplied: 0,
      abandoned: true,
    };
  }

  if (isVictory(info.outcome)) {
    const ratio = addBoonRegenRatio(run, Math.max(0, info.allyHpSum / info.allyHpMaxSum));
    // Step 10b-4: boss victory flips the run-level `cleared` flag so the
    // page can render DungeonClearPage. Non-boss victories leave the
    // existing cleared value (typically false) alone.
    const cleared = info.isBoss === true ? true : run.cleared;
    return {
      nextRun: { ...run, partyHpRatio: ratio, cleared },
      partyHpRatio: ratio,
      reviveConsumed: false,
      penaltyApplied: 0,
      abandoned: false,
    };
  }

  // Defeat path.
  if (info.allyHpSum > 0) {
    // Mixed party: some allies survived. Multi-hero future will handle the
    // per-hero revive ladder here; for 10b-3 scope we just write back the
    // ratio without applying penalty / revive (1-hero never reaches this
    // branch because the lone hero is either alive or dead).
    const ratio = addBoonRegenRatio(run, info.allyHpSum / info.allyHpMaxSum);
    return {
      nextRun: { ...run, partyHpRatio: ratio },
      partyHpRatio: ratio,
      reviveConsumed: false,
      penaltyApplied: 0,
      abandoned: false,
    };
  }

  // allyHpSum === 0 — every ally is down. Apply docs/07 §7.5.1 revival.
  const nextInventory = removeOneFromInventory(run.inventory, REVIVE_POTION_ID);
  const reviveConsumed = nextInventory !== run.inventory;
  if (reviveConsumed) {
    return {
      nextRun: { ...run, partyHpRatio: REVIVE_HP_RATIO, inventory: nextInventory },
      partyHpRatio: REVIVE_HP_RATIO,
      reviveConsumed: true,
      penaltyApplied: 0,
      abandoned: false,
    };
  }

  // HP=1 revive + penalty +30% (cap 50%).
  const ratio = 1 / info.allyHpMaxSum;
  const penaltyApplied = Math.max(
    0,
    Math.min(PENALTY_INCREMENT, PENALTY_CAP - run.penaltyPct),
  );
  const penaltyPct = run.penaltyPct + penaltyApplied;
  return {
    nextRun: {
      ...run,
      partyHpRatio: ratio,
      penaltyPct,
    },
    partyHpRatio: ratio,
    reviveConsumed: false,
    penaltyApplied,
    abandoned: false,
  };
}

function addBoonRegenRatio(run: DungeonRunState, baseRatio: number): number {
  const regen = aggregateDungeonBoons(run.activeBoons).regenRatio;
  return Math.min(1, Math.max(0, baseRatio + regen));
}
